import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiRequestError } from '@/api/client';
import { useLogin } from '@/api/queries';

interface FormValues {
  email: string;
  password: string;
}

interface LoginFormProps {
  defaultEmail?: string;
  onSuccess: () => void;
}

export function LoginForm({ defaultEmail, onSuccess }: LoginFormProps) {
  const login = useLogin();
  const { register, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { email: defaultEmail ?? '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      onSuccess();
    } catch {
      // Error rendered below via login.error
    }
  });

  const error =
    login.error instanceof ApiRequestError ? login.error.body.message : login.error?.message;

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus={!defaultEmail}
          {...register('email', { required: true })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          autoFocus={!!defaultEmail}
          {...register('password', { required: true })}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" className="w-full" disabled={formState.isSubmitting || login.isPending}>
        {login.isPending ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );
}
